import { useState, useRef, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Search, X, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { useT } from "../lib/LangContext";
import { ISLAND_BY_SLUG } from "../lib/constants";

const slugify = (s) =>
  (s || "").toLowerCase().replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").slice(0, 60);

export const SearchBox = () => {
  const { t, lang } = useT();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [popular, setPopular] = useState([]);
  const inputRef = useRef(null);
  const wrapRef = useRef(null);

  // Close on outside click + ESC
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onClick);
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 30);
      // Fetch top queries lazily — cached client-side per session by the hook.
      if (popular.length === 0) {
        api.get("/searches/popular", { params: { limit: 8 } })
          .then((r) => setPopular(r.data || []))
          .catch(() => {});
      }
    }
  }, [open, popular.length]);

  // Debounced fetch
  const search = useCallback(async (query, language) => {
    if ((query || "").trim().length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const r = await api.get("/articles/search", {
        params: { q: query, lang: language, limit: 8 },
      });
      setResults(r.data);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const id = setTimeout(() => search(q, lang), 220);
    return () => clearTimeout(id);
  }, [q, lang, search]);

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={t("search") || "Search"}
        className="p-2 rounded-full text-[#6B7280] hover:text-[#1A1A1A] hover:bg-[#FAFAFA] transition-colors"
        data-testid="header-search-btn"
      >
        <Search size={18} />
      </button>

      {open && (
        <div
          className="fixed inset-0 sm:absolute sm:inset-auto sm:right-0 sm:top-full sm:mt-2 z-50 bg-white sm:rounded-2xl sm:shadow-2xl sm:border sm:border-[#EEEEEE] sm:w-[440px]"
          data-testid="search-overlay"
        >
          <div className="flex items-center gap-2 px-4 sm:px-3 py-3 sm:py-2.5 border-b border-[#EEEEEE]">
            <Search size={16} className="text-[#6B7280] shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("search_placeholder") || "Search headlines…"}
              className="flex-1 bg-transparent border-0 outline-none text-sm text-[#1A1A1A] placeholder-[#9CA3AF] py-1"
              data-testid="search-input"
            />
            {loading && <Loader2 size={14} className="animate-spin text-[#6B7280]" />}
            <button
              onClick={() => setOpen(false)}
              aria-label="Close"
              className="p-1 text-[#6B7280] hover:text-[#1A1A1A]"
              data-testid="search-close-btn"
            >
              <X size={18} />
            </button>
          </div>

          <div className="max-h-[70vh] sm:max-h-[420px] overflow-y-auto" data-testid="search-results">
            {q.trim().length < 2 ? (
              <div className="px-4 py-6" data-testid="search-hint-block">
                <div className="text-sm text-[#6B7280] mb-3 text-center">
                  {t("search_hint") || "Type at least 2 characters to search…"}
                </div>
                {popular.length > 0 && (
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-[#9CA3AF] mb-2">
                      {t("search_trending")}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {popular.map((p) => (
                        <button
                          key={p.q}
                          onClick={() => setQ(p.q)}
                          className="px-3 py-1 rounded-full bg-[#F5F5F5] hover:bg-[#0AADA0]/10 hover:text-[#0AADA0] text-xs text-[#1A2E35] transition-colors capitalize"
                          data-testid={`popular-search-${p.q.replace(/\s+/g, "-")}`}
                        >
                          {p.q}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : results.length === 0 && !loading ? (
              <div className="px-4 py-8 text-center text-sm text-[#6B7280]" data-testid="search-empty">
                {t("search_empty")}
              </div>
            ) : (
              <ul>
                {results.map((a) => {
                  const island = ISLAND_BY_SLUG[a.island];
                  return (
                    <li key={a.id}>
                      <Link
                        to={`/article/${a.id}-${slugify(a.title)}?lang=${lang}`}
                        onClick={() => setOpen(false)}
                        className="flex items-start gap-3 px-4 py-3 hover:bg-[#FAFAFA] border-b border-[#F5F5F5] last:border-0"
                        data-testid={`search-result-${a.id}`}
                      >
                        {a.image_url ? (
                          <img
                            src={a.image_url}
                            alt=""
                            className="w-14 h-14 rounded-lg object-cover bg-[#F5F5F5] flex-shrink-0"
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-14 h-14 rounded-lg bg-[#F5F5F5] flex-shrink-0 flex items-center justify-center text-xl">
                            {island?.flag || "📰"}
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="text-[13px] font-medium text-[#1A2E35] line-clamp-2 leading-snug">
                            {a.title}
                          </div>
                          <div className="text-[11px] text-[#6B7280] mt-1 flex items-center gap-1">
                            <span>{island?.flag}</span>
                            <span>{island?.name || a.island}</span>
                            <span>·</span>
                            <span className="truncate">{a.source_name}</span>
                          </div>
                        </div>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
